/**
 * Vertical thumbnail scrolling for pi-product-gallery.
 * Patches thumbnail slider + media-gallery active-thumb scroll (top/left).
 */
(function () {
  const VERTICAL_SELECTOR = 'media-gallery.pi-product-gallery';

  function isVerticalThumbSlider(component) {
    return component && component.classList.contains('pi-thumbnail-slider');
  }

  function isSlideVisibleVertical(slider, element, offset = 0) {
    const lastVisible = slider.scrollTop + slider.clientHeight - offset;
    return element.offsetTop + element.clientHeight <= lastVisible && element.offsetTop >= slider.scrollTop;
  }

  function initVerticalThumbnailSlider(component) {
    if (component.__piVerticalInit) return;
    component.__piVerticalInit = true;

    const slider = component.querySelector('[id^="Slider-"]');
    const items = component.querySelectorAll('[id^="Slide-"]');
    const prevButton = component.querySelector('button[name="previous"]');
    const nextButton = component.querySelector('button[name="next"]');

    if (!slider || !prevButton || !nextButton) return;

    const visibleItems = () => Array.from(items).filter((el) => el.offsetHeight > 0 && getComputedStyle(el).display !== 'none');

    function canScroll() {
      return slider.scrollHeight > slider.clientHeight + 1;
    }

    function getItemOffset() {
      const shown = visibleItems();
      if (shown.length < 2) return shown[0]?.offsetHeight + parseFloat(getComputedStyle(slider).gap || 0) || 0;
      return shown[1].offsetTop - shown[0].offsetTop;
    }

    function updateButtons() {
      const scrollThreshold = 1;
      const scrollable = canScroll();
      const atTop = slider.scrollTop <= scrollThreshold;
      const atBottom = slider.scrollTop + slider.clientHeight >= slider.scrollHeight - scrollThreshold;
      const showUp = scrollable && !atTop;

      component.classList.toggle('can-scroll', scrollable);
      component.classList.toggle('is-scrolled', showUp);
      component.classList.toggle('is-at-end', !scrollable || atBottom);

      prevButton.toggleAttribute('disabled', !showUp);
      prevButton.hidden = !showUp;

      if (!scrollable || atBottom) {
        nextButton.setAttribute('disabled', 'disabled');
        nextButton.hidden = true;
      } else {
        nextButton.removeAttribute('disabled');
        nextButton.hidden = false;
      }
    }

    function scrollByStep(direction) {
      const step = parseInt(prevButton.dataset.step || nextButton.dataset.step || '1', 10);
      const offset = getItemOffset();
      const target =
        direction === 'next'
          ? slider.scrollTop + step * offset
          : slider.scrollTop - step * offset;
      slider.scrollTo({ top: target, behavior: 'smooth' });
    }

    const onPrev = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      scrollByStep('prev');
    };

    const onNext = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      scrollByStep('next');
    };

    prevButton.addEventListener('click', onPrev, true);
    nextButton.addEventListener('click', onNext, true);

    slider.addEventListener('scroll', updateButtons, { passive: true });
    new ResizeObserver(updateButtons).observe(slider);
    slider.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', updateButtons, { once: true });
    });
    updateButtons();
    requestAnimationFrame(updateButtons);
    window.addEventListener('load', updateButtons, { once: true });
  }

  function patchMediaGallery(gallery) {
    if (gallery.__piVerticalPatched) return;
    gallery.__piVerticalPatched = true;

    const originalSetActive = gallery.setActiveThumbnail?.bind(gallery);
    if (!originalSetActive) return;

    gallery.setActiveThumbnail = function (thumbnail) {
      if (!this.elements?.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((el) => el.removeAttribute('aria-current'));
      thumbnail.querySelector('button')?.setAttribute('aria-current', true);

      const thumbSlider = this.elements.thumbnails.querySelector('[id^="Slider-"]');
      if (!thumbSlider) return;

      if (isSlideVisibleVertical(thumbSlider, thumbnail, 10)) return;
      thumbSlider.scrollTo({ top: thumbnail.offsetTop, behavior: 'smooth' });
    };
  }

  function getActiveMediaContainer(gallery) {
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return null;
    return (
      viewer.querySelector('.product__media-item.is-active .product-media-container') ||
      viewer.querySelector('.product-media-container')
    );
  }

  function syncThumbColumnHeight(gallery) {
    const thumbSlider = gallery.querySelector('.pi-thumbnail-slider');
    const mediaContainer = getActiveMediaContainer(gallery);
    if (!thumbSlider || !mediaContainer) return;

    const height = Math.round(mediaContainer.getBoundingClientRect().height);
    if (height <= 0) return;

    gallery.style.setProperty('--pi-pg-thumb-column-height', `${height}px`);
  }

  function bindThumbHeightSync(gallery) {
    if (gallery.__piThumbHeightBound) return;
    gallery.__piThumbHeightBound = true;

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;

    let mediaObserver = null;
    let observedMedia = null;

    const scheduleSync = () => requestAnimationFrame(() => syncThumbColumnHeight(gallery));

    function observeActiveMedia() {
      const mediaContainer = getActiveMediaContainer(gallery);
      if (mediaContainer === observedMedia) return;
      if (mediaObserver && observedMedia) mediaObserver.unobserve(observedMedia);
      if (!mediaContainer) return;
      if (!mediaObserver) mediaObserver = new ResizeObserver(scheduleSync);
      mediaObserver.observe(mediaContainer);
      observedMedia = mediaContainer;
      scheduleSync();
    }

    observeActiveMedia();
    window.addEventListener('resize', scheduleSync, { passive: true });
    window.addEventListener('load', scheduleSync, { once: true });

    new MutationObserver(observeActiveMedia).observe(viewer, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });

    viewer.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', scheduleSync, { once: true });
    });
  }

  function initGallery(gallery) {
    const thumbComponent = gallery.querySelector('.pi-thumbnail-slider');
    if (thumbComponent) initVerticalThumbnailSlider(thumbComponent);

    bindThumbHeightSync(gallery);

    if (gallery.setActiveThumbnail) {
      patchMediaGallery(gallery);
    } else {
      const observer = new MutationObserver(() => {
        if (gallery.setActiveThumbnail) {
          patchMediaGallery(gallery);
          observer.disconnect();
        }
      });
      observer.observe(gallery, { childList: true, subtree: true });
    }
  }

  function initAll() {
    document.querySelectorAll(VERTICAL_SELECTOR).forEach(initGallery);
  }

  function bootstrap() {
    if (customElements.get('media-gallery')) {
      initAll();
    } else {
      customElements.whenDefined('media-gallery').then(initAll);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.(VERTICAL_SELECTOR).forEach(initGallery);
  });
})();
